import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Dec } from '../numeric/decimal.js';
import { inventoryKeys } from './keys.js';
import { useInventoryClient } from './provider.js';
import { useIdempotentMutation } from './useIdempotentMutation.js';
export function useUoms(options) {
    const client = useInventoryClient();
    return useQuery({
        queryKey: inventoryKeys.uoms.list(),
        queryFn: ({ signal }) => client.uoms.list(signal),
        // Units of measure change about once a year. Caching them for the session avoids a
        // request per row on any screen that renders a quantity with its unit.
        staleTime: 5 * 60_000,
        ...options,
    });
}
/** A lookup by id, built from the same cached list. */
export function useUomIndex() {
    const { data } = useUoms();
    return new Map((data ?? []).map((u) => [u.id, u]));
}
export function useUomConversions(itemId, options) {
    const client = useInventoryClient();
    return useQuery({
        queryKey: inventoryKeys.uoms.conversions(itemId),
        queryFn: ({ signal }) => client.uoms.listConversions(itemId, signal),
        ...options,
    });
}
export function useCreateUom() {
    const client = useInventoryClient();
    const queryClient = useQueryClient();
    return useIdempotentMutation({
        mutationFn: (input, operation) => client.uoms.create(input, operation),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: inventoryKeys.uoms.all() });
        },
    });
}
export function useCreateUomConversion() {
    const client = useInventoryClient();
    const queryClient = useQueryClient();
    return useIdempotentMutation({
        mutationFn: (input, operation) => client.uoms.createConversion(input, operation),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: inventoryKeys.uoms.all() });
        },
    });
}
/**
 * Convert a quantity between units, on the server.
 *
 * This is a query rather than a helper because the answer must be the server's answer.
 * Conversion chains compose, item overrides beat global rules, and rounding happens once
 * at the end — reimplementing any of it in the browser gives the picker one number and the
 * ledger another, and the difference only shows up as a stock discrepancy.
 */
export function useConvertQuantity(params, options) {
    const client = useInventoryClient();
    const ready = Boolean(params.quantity && params.from_uom_id && params.to_uom_id);
    const quantityText = params.quantity ? Dec.parse(params.quantity).toString() : '';
    return useQuery({
        queryKey: inventoryKeys.uoms.convert({
            quantity: quantityText,
            from_uom_id: params.from_uom_id ?? '',
            to_uom_id: params.to_uom_id ?? '',
            ...(params.item_id ? { item_id: params.item_id } : {}),
        }),
        queryFn: ({ signal }) => client.uoms.convert({
            quantity: quantityText,
            from_uom_id: params.from_uom_id,
            to_uom_id: params.to_uom_id,
            ...(params.item_id ? { item_id: params.item_id } : {}),
        }, signal),
        enabled: ready,
        ...options,
    });
}
//# sourceMappingURL=uom.js.map